import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared-kernel/primary/shared/shadcn/ui/table";

interface DataTableSkeletonRowsProps {
  columnCount: number;
  pageSize?: number;
}

export function DataTableSkeletonRows({ columnCount, pageSize = 10 }: DataTableSkeletonRowsProps) {
  return (
    <>
      {Array.from({ length: pageSize }).map((_, rowIndex) => (
        <TableRow key={`skeleton-${rowIndex}`}>
          {Array.from({ length: columnCount }).map((_, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton className="h-6 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

interface DataTableLoadingProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  pageSize?: number;
  invisibleColumns?: VisibilityState;
}

export function DataTableLoading<TData, TValue>({ columns, pageSize = 10, invisibleColumns = {} }: DataTableLoadingProps<TData, TValue>) {
  // Filter out invisible columns to match the loaded state
  const visibleColumns = columns.filter(column => {
    const columnId = "id" in column && column.id ? column.id : "accessorKey" in column ? column.accessorKey : undefined;
    if (!columnId) return true;
    return invisibleColumns[columnId as string] !== false;
  });

  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <div className="w-72">
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
      <div className="rounded-md border p-1">
        <Table style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((column, index) => {
                const { meta } = column;
                const { width } = meta ?? { width: null };
                return (
                  <TableHead
                    key={index}
                    style={{
                      width: width ? width : "auto",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {column.meta?.title ?? ""}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            <DataTableSkeletonRows columnCount={visibleColumns.length} pageSize={pageSize} />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
